// Minimal fetch wrapper for the TkTuner API. Gzips JSON request bodies when
// asked (sync payloads compress ~8x).
import { gzipSync } from "node:zlib";

export interface ApiResponse {
  status: number;
  json: Record<string, unknown>;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

interface RequestOpts {
  body?: unknown;
  token?: string;
  gzip?: boolean;
}

export async function apiRequest(
  method: "GET" | "POST" | "DELETE",
  apiUrl: string,
  path: string,
  opts: RequestOpts = {},
): Promise<ApiResponse> {
  const headers: Record<string, string> = { accept: "application/json" };
  if (opts.token) headers.authorization = `Bearer ${opts.token}`;
  let body: string | Buffer | undefined;
  if (opts.body !== undefined) {
    headers["content-type"] = "application/json";
    const raw = JSON.stringify(opts.body);
    if (opts.gzip) {
      headers["content-encoding"] = "gzip";
      body = gzipSync(Buffer.from(raw, "utf8"));
    } else {
      body = raw;
    }
  }
  const res = await fetch(`${apiUrl.replace(/\/$/, "")}${path}`, { method, headers, body });
  let json: Record<string, unknown> = {};
  try {
    json = (await res.json()) as Record<string, unknown>;
  } catch {
    // 204s and empty bodies are fine
  }
  return { status: res.status, json };
}

/** Throws ApiError on any non-2xx (use apiRequest directly to branch on status). */
export async function apiCall(
  method: "GET" | "POST" | "DELETE",
  apiUrl: string,
  path: string,
  opts: RequestOpts = {},
): Promise<Record<string, unknown>> {
  const { status, json } = await apiRequest(method, apiUrl, path, opts);
  if (status >= 200 && status < 300) return json;
  const err = (json.error ?? {}) as { code?: string; message?: string };
  throw new ApiError(status, err.code ?? "UNKNOWN", err.message ?? `HTTP ${status}`);
}
